export const LANGS = ["de", "en"] as const;
export type Lang = (typeof LANGS)[number];

/** Canonical origin for absolute canonical, og:url and hreflang hrefs. */
export const SITE_ORIGIN = "https://retatrutide-thailand.org";

export interface HreflangInput {
  /** Route path without query, e.g. "/wissenschaft" */
  path: string;
}

/** Build an absolute URL from a route path (and optional ?lang). */
function absUrl(path: string, lang?: Lang) {
  const clean = path === "/" ? "" : path;
  return lang ? `${SITE_ORIGIN}${clean || "/"}?lang=${lang}` : `${SITE_ORIGIN}${clean || "/"}`;
}

/**
 * Build hreflang link entries for a route (absolute URLs, as Google requires).
 * - de and en variants use `?lang=` to disambiguate.
 * - en-TH / de-TH group the same content for users in Thailand.
 * - x-default points to the unparameterised path (auto-detects language).
 */
export function hreflangLinks({ path }: HreflangInput) {
  return [
    { rel: "alternate", hreflang: "de", href: absUrl(path, "de") },
    { rel: "alternate", hreflang: "en", href: absUrl(path, "en") },
    { rel: "alternate", hreflang: "en-TH", href: absUrl(path, "en") },
    { rel: "alternate", hreflang: "de-TH", href: absUrl(path, "de") },
    { rel: "alternate", hreflang: "x-default", href: absUrl(path) },
  ];
}


interface PageCopy {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  breadcrumbName: string;
}

/** Per-route, per-language SEO copy. Keep in sync with visible content. */
export const pageMeta: Record<string, Record<Lang, PageCopy>> = {
  "/": {
    de: {
      title: "Retatrutid Thailand – ärztlich begleitete Behandlung in Bangkok",
      description:
        "Retatrutid Thailand: ärztlich begleitete Behandlung mit der neuen GLP-1/GIP/Glucagon-Generation über eine Partnerklinik in Bangkok. Legaler Zugang, persönliche Betreuung DE/EN, kostenlose Erstberatung.",
      ogTitle: "Retatrutid Thailand – ärztlich begleitete Behandlung",
      ogDescription:
        "Legaler Zugang zur neuen GLP-1/GIP/Glucagon-Generation über unsere Partnerklinik in Bangkok. Persönliche Betreuung, kostenlose Erstberatung.",
      breadcrumbName: "Startseite",
    },
    en: {
      title: "Retatrutide Thailand | Medically Supervised Treatment in Bangkok",
      description:
        "Discover Retatrutide for effective weight management in Bangkok. Safe, medically supervised treatments tailored to your goals. Learn more and visit us!",
      ogTitle: "Retatrutide Thailand | Medically Supervised Treatment in Bangkok",
      ogDescription:
        "Discover Retatrutide for effective weight management in Bangkok. Safe, medically supervised treatments tailored to your goals. Learn more and visit us!",
      breadcrumbName: "Home",
    },
  },
  "/wissenschaft": {
    de: {
      title: "Wirkmechanismus – Retatrutid Triple-Agonist (GLP-1 / GIP / Glucagon)",
      description:
        "Das Triple-Agonist-Prinzip von Retatrutid: gleichzeitige Aktivierung von GLP-1-, GIP- und Glucagon-Rezeptoren für Sättigung, Insulin-Sensitivität und Energieumsatz.",
      ogTitle: "Wirkmechanismus – Retatrutid",
      breadcrumbName: "Wirkmechanismus",
    },
    en: {
      title: "Mechanism of Action – Retatrutide triple-agonist (GLP-1 / GIP / Glucagon)",
      description:
        "The triple-agonist principle of Retatrutide: simultaneous activation of GLP-1, GIP and glucagon receptors for satiety, insulin sensitivity and energy expenditure.",
      ogTitle: "Mechanism of Action – Retatrutide",
      breadcrumbName: "Mechanism",
    },
  },
  "/studien": {
    de: {
      title: "Klinische Studien – TRIUMPH-Programm & Phase-III-Daten zu Retatrutid",
      description:
        "Phase-II- und Phase-III-Ergebnisse zu Retatrutid: bis zu 24 % durchschnittlicher Gewichtsverlust nach 48 Wochen. Vergleich mit Semaglutid und Tirzepatid.",
      ogTitle: "Retatrutid – Klinische Studiendaten",
      breadcrumbName: "Klinische Studien",
    },
    en: {
      title: "Clinical Studies – TRIUMPH program & Phase III data | Retatrutide",
      description:
        "Phase II and Phase III results for Retatrutide: up to 24% average weight loss after 48 weeks. Comparison with Semaglutide and Tirzepatide.",
      ogTitle: "Retatrutide – Clinical study data",
      breadcrumbName: "Clinical Studies",
    },
  },
  "/rechner": {
    de: {
      title: "Gewichtsverlust-Rechner – Retatrutid vs Tirzepatid vs Semaglutid",
      description:
        "Interaktiver Rechner: prognostizierter Gewichtsverlust mit Retatrutid, Tirzepatid und Semaglutid auf Basis klinischer Studiendaten.",
      ogTitle: "Retatrutid Gewichtsverlust-Rechner",
      breadcrumbName: "Rechner",
    },
    en: {
      title: "Weight-Loss Calculator – Retatrutide vs Tirzepatide vs Semaglutide",
      description:
        "Interactive calculator: projected weight loss with Retatrutide, Tirzepatide and Semaglutide based on clinical study data.",
      ogTitle: "Retatrutide weight-loss calculator",
      breadcrumbName: "Calculator",
    },
  },
  "/sicherheit": {
    de: {
      title: "Sicherheit & Nebenwirkungen – Retatrutid Warnhinweise",
      description:
        "Sicherheitsprofil von Retatrutid: gastrointestinale Nebenwirkungen, Kontraindikationen, Warnhinweise. Zugang ist über genehmigte klinische Studienprogramme möglich.",
      ogTitle: "Retatrutid – Sicherheit & Nebenwirkungen",
      breadcrumbName: "Sicherheit",
    },
    en: {
      title: "Safety & Side Effects – Retatrutide warnings and contraindications",
      description:
        "Safety profile of Retatrutide: gastrointestinal side effects, contraindications, warnings. Access is available through approved clinical study programs.",
      ogTitle: "Retatrutide – Safety & side effects",
      breadcrumbName: "Safety",
    },
  },
  "/faq": {
    de: {
      title: "Retatrutid FAQ – Wirkung, Zulassung, Nebenwirkungen & Vergleich",
      description:
        "Häufige Fragen zu Retatrutid (LY3437943): Wirkmechanismus, Zulassung 2026, Nebenwirkungen und Vergleich mit Semaglutid und Tirzepatid – wissenschaftlich beantwortet.",
      ogTitle: "Retatrutid FAQ – Alle Fragen kompakt",
      breadcrumbName: "FAQ",
    },
    en: {
      title: "Retatrutide FAQ – Effects, approval, side effects & comparison",
      description:
        "Frequently asked questions on Retatrutide (LY3437943): mechanism, 2026 approval, side effects and comparison with Semaglutide and Tirzepatide.",
      ogTitle: "Retatrutide FAQ – All questions in one place",
      breadcrumbName: "FAQ",
    },
  },
  "/vergleich": {
    de: {
      title: "Retatrutid vs Semaglutid vs Tirzepatid – Vergleich 2026",
      description:
        "Direkter Vergleich: Retatrutid, Semaglutid (Wegovy, Ozempic) und Tirzepatid (Mounjaro, Zepbound). Mechanismus, Gewichtsverlust, Zulassungsstatus 2026.",
      ogTitle: "Retatrutid vs Semaglutid vs Tirzepatid",
      breadcrumbName: "Vergleich",
    },
    en: {
      title: "Retatrutide vs Semaglutide vs Tirzepatide – 2026 comparison",
      description:
        "Head-to-head: Retatrutide, Semaglutide (Wegovy, Ozempic) and Tirzepatide (Mounjaro, Zepbound). Mechanism, weight loss, 2026 approval status.",
      ogTitle: "Retatrutide vs Semaglutide vs Tirzepatide",
      breadcrumbName: "Comparison",
    },
  },
  "/reta-vs-ozempic": {
    de: {
      title: "Reta vs Ozempic – Warum Retatrutide als stärkere nächste Generation gilt",
      description:
        "Reta vs Ozempic: Retatrutide ist als Triple-Agonist (GLP-1, GIP, Glucagon) die potenziell stärkere Weiterentwicklung gegenüber Semaglutid (Ozempic, Wegovy). Wirkmechanismus, Gewichtsverlust, Nebenwirkungen und Studienlage im Vergleich.",
      ogTitle: "Reta vs Ozempic – Die stärkere nächste Generation",
      ogDescription:
        "Retatrutide vs Ozempic: Triple-Agonist gegen GLP-1-Mono. Wirkung, Gewichtsverlust, Nebenwirkungen und Zulassung im direkten Vergleich.",
      breadcrumbName: "Reta vs Ozempic",
    },
    en: {
      title: "Reta vs Ozempic – Why Retatrutide is the stronger next generation",
      description:
        "Reta vs Ozempic: as a triple agonist (GLP-1, GIP, glucagon), Retatrutide is the potentially stronger evolution beyond Semaglutide (Ozempic, Wegovy). Mechanism, weight loss, side effects and trial data compared.",
      ogTitle: "Reta vs Ozempic – The stronger next generation",
      ogDescription:
        "Retatrutide vs Ozempic: triple agonist vs GLP-1 mono. Effect, weight loss, side effects and approval compared side by side.",
      breadcrumbName: "Reta vs Ozempic",
    },
  },
  "/promis": {
    de: {
      title: "Retatrutide & Promis – Ozempic-Stars & warum Reta die nächste Generation ist",
      description:
        "Von Oprah bis Elon Musk: Welche Promis öffentlich über Ozempic, Wegovy und Mounjaro gesprochen haben – und warum es bei Retatrutide (Reta, LY3437943) noch keine seriös bestätigten Prominenten gibt.",
      ogTitle: "Retatrutide & Promis – Ozempic-Stars & die neue Generation",
      ogDescription:
        "Oprah, Elon Musk, Whoopi Goldberg & Co: Promi-Erfahrungen mit GLP-1 – und wo Retatrutide (Reta Peptid) steht.",
      breadcrumbName: "Promis",
    },
    en: {
      title: "Retatrutide & celebrities – Ozempic stars & why Reta is the next generation",
      description:
        "From Oprah to Elon Musk: which celebrities have publicly discussed Ozempic, Wegovy and Mounjaro — and why there are no credibly confirmed celebrity users of Retatrutide (Reta, LY3437943) yet.",
      ogTitle: "Retatrutide & celebrities – Ozempic stars & the new generation",
      ogDescription:
        "Oprah, Elon Musk, Whoopi Goldberg & co: celebrity GLP-1 experiences — and where Retatrutide (Reta peptide) stands.",
      breadcrumbName: "Celebrities",
    },
  },
  "/zulassung": {
    de: {
      title: "Retatrutid Zulassung 2026 – FDA, EMA & Deutschland Status",
      description:
        "Wann kommt die Zulassung von Retatrutid? Aktueller Status bei FDA, EMA und BfArM, Timeline bis 2026/27 und Details zur TRIUMPH-1-Phase-III-Studie.",
      ogTitle: "Retatrutid Zulassung 2026 – FDA & EMA Timeline",
      breadcrumbName: "Zulassung",
    },
    en: {
      title: "Retatrutide approval 2026 – FDA, EMA & Germany status",
      description:
        "When will Retatrutide be approved? Current FDA, EMA and BfArM status, timeline through 2026/27 and details on the TRIUMPH-1 Phase III trial.",
      ogTitle: "Retatrutide approval 2026 – FDA & EMA timeline",
      breadcrumbName: "Approval",
    },
  },
  "/nebenwirkungen": {
    de: {
      title: "Retatrutid Nebenwirkungen – GI-Effekte, Herzfrequenz & Risiken",
      description:
        "Retatrutid Nebenwirkungen im Detail: Übelkeit, Erbrechen, Durchfall, Herzfrequenz-Anstieg, seltene Risiken und Kontraindikationen – auf Basis der Phase-II-Daten.",
      ogTitle: "Retatrutid – Nebenwirkungen & Risiken",
      breadcrumbName: "Nebenwirkungen",
    },
    en: {
      title: "Retatrutide side effects – GI effects, heart rate & risks",
      description:
        "Retatrutide side effects in detail: nausea, vomiting, diarrhea, heart-rate increase, rare risks and contraindications — based on Phase II data.",
      ogTitle: "Retatrutide – side effects & risks",
      breadcrumbName: "Side effects",
    },
  },
  "/thailand": {
    de: {
      title: "Retatrutid & GLP-1 in Thailand – Guide für Expats 2026",
      description:
        "Für Ausländer in Thailand: Verfügbarkeit von Retatrutid, Wegovy und Mounjaro, Thai FDA (อย.), Bangkoker Krankenhäuser (Bumrungrad, Samitivej, BNH) und Preise in Baht.",
      ogTitle: "Retatrutid & GLP-1 in Thailand – Expat Guide",
      breadcrumbName: "Thailand",
    },
    en: {
      title: "Retatrutide & GLP-1 in Thailand – Expat guide 2026",
      description:
        "For foreigners living in Thailand: availability of Retatrutide, Wegovy and Mounjaro, Thai FDA (อย.), Bangkok hospitals (Bumrungrad, Samitivej, BNH) and prices in Baht.",
      ogTitle: "Retatrutide & GLP-1 in Thailand – Expat guide",
      breadcrumbName: "Thailand",
    },
  },
  "/ueber-uns": {
    de: {
      title: "Über uns – Deutsch-thailändisches Ärzte- & Wissenschaftsteam | Retatrutid Thailand",
      description:
        "Lernen Sie unser Team kennen: deutsche und thailändische Endokrinologen, Adipositas-Mediziner, klinische Pharmakologen und Peptidchemiker – gemeinsam für die ärztlich begleitete Retatrutid-Behandlung in Bangkok.",
      ogTitle: "Über uns – deutsch-thailändisches Ärzte- & Wissenschaftsteam",
      ogDescription:
        "Endokrinologen, Adipositas-Mediziner, klinische Pharmakologen und Peptidchemiker aus Deutschland und Thailand – gemeinsam für Retatrutid-Behandlung mit Studienprotokoll in Bangkok.",
      breadcrumbName: "Über uns",
    },
    en: {
      title: "About us – German-Thai team of physicians & scientists | Retatrutide Thailand",
      description:
        "Meet our team: German and Thai endocrinologists, obesity physicians, clinical pharmacologists and peptide chemists — together for medically supervised Retatrutide treatment in Bangkok.",
      ogTitle: "About us – German-Thai team of physicians & scientists",
      ogDescription:
        "Endocrinologists, obesity physicians, clinical pharmacologists and peptide chemists from Germany and Thailand — together for Retatrutide treatment under a clinical protocol in Bangkok.",
      breadcrumbName: "About us",
    },
  },
  "/kontakt": {
    de: {
      title: "Beratung anfragen – Retatrutid & GLP-1-Therapie | Retatrutid Thailand",
      description:
        "Kostenloses Erstgespräch zu Retatrutid, Wegovy und Mounjaro. Persönliche Beratung durch ein spezialisiertes Ärzteteam – Kontaktformular und Rückruf möglich.",
      ogTitle: "Beratung anfragen – Retatrutid & GLP-1-Therapie | Retatrutid Thailand",
      breadcrumbName: "Kontakt",
    },
    en: {
      title: "Request a consultation – Retatrutide & GLP-1 therapy | Retatrutid Thailand",
      description:
        "Free initial consultation on Retatrutide, Wegovy and Mounjaro. Personal advice from a specialised medical team – contact form and callback available.",
      ogTitle: "Request a consultation – Retatrutide & GLP-1 therapy | Retatrutid Thailand",
      breadcrumbName: "Contact",
    },
  },
};

/** validateSearch helper: keeps only ?lang=de|en. */
export function validateLangSearch(search: Record<string, unknown>): { lang?: Lang } {
  const l = search.lang;
  return l === "de" || l === "en" ? { lang: l } : {};
}

/**
 * Build fully localized head() output for a route based on ?lang= search.
 * - lang provided → self-referencing canonical + og:url on the ?lang=XX variant.
 * - lang absent   → canonical on the clean path (x-default), copy defaults to EN.
 */
import { ogImages } from "./og-images";

export function localizedHead(path: keyof typeof pageMeta, lang: Lang | undefined) {
  const effective: Lang = lang ?? "en";
  const copy = pageMeta[path][effective];
  const canonical = absUrl(path, lang);
  const ogLocale = effective === "de" ? "de_DE" : "en_US";
  const ogLocaleAlt = effective === "de" ? "en_US" : "de_DE";
  const ogType = path === "/" ? "website" : "article";
  const ogTitle = copy.ogTitle ?? copy.title;
  const ogDescription = copy.ogDescription ?? copy.description;

  const meta: Array<Record<string, string>> = [
    { title: copy.title },
    { name: "description", content: copy.description },
    { property: "og:title", content: ogTitle },
    { property: "og:description", content: ogDescription },
    { property: "og:url", content: canonical },
    { property: "og:type", content: ogType },
    { property: "og:locale", content: ogLocale },
    { property: "og:locale:alternate", content: ogLocaleAlt },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: ogTitle },
    { name: "twitter:description", content: ogDescription },
  ];

  // Immer ein og:image ausliefern — Facebook/Twitter/LinkedIn fallen sonst
  // auf beliebige Seitenbilder zurück. Fallback: Home-OG (1200×630).
  const image = ogImages[path] ?? ogImages["/"];
  const alt = image.alt[effective];
  meta.push(
    { property: "og:image", content: image.url },
    { property: "og:image:secure_url", content: image.url },
    { property: "og:image:type", content: "image/jpeg" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: alt },
    { name: "twitter:image", content: image.url },
    { name: "twitter:image:alt", content: alt },
  );

  return {
    meta,
    links: [
      { rel: "canonical", href: canonical },
      ...hreflangLinks({ path }),
    ],
  };
}


/**
 * Build a BreadcrumbList schema with "Retatrutid Thailand" as the first item.
 * Deep routes get a second item for the current page; the home route gets a
 * single-item breadcrumb.
 */
export function breadcrumbSchema(path: keyof typeof pageMeta, lang: Lang | undefined) {
  const effective: Lang = lang ?? "en";
  const copy = pageMeta[path][effective];
  const clean = path === "/" ? "/" : path;

  const itemListElement: Record<string, unknown>[] = [
    { "@type": "ListItem", position: 1, name: "Retatrutid Thailand", item: `${SITE_ORIGIN}/` },
  ];

  if (path !== "/") {
    itemListElement.push({
      "@type": "ListItem",
      position: 2,
      name: copy.breadcrumbName,
      item: `${SITE_ORIGIN}${clean}`,
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  };
}

/**
 * MedicalWebPage schema for medical-topic routes (mechanism, safety, side
 * effects, studies, approval, calculator). Uses per-route localized title
 * and description so Google can render richer results.
 */
export function medicalWebPageSchema(
  path: keyof typeof pageMeta,
  lang: Lang | undefined,
  opts: {
    specialty?: "Endocrine" | "Pharmacologic" | "Internal";
    aspect?:
      | "MayTreat"
      | "Treatment"
      | "Prevention"
      | "SideEffects"
      | "Contraindication"
      | "Prognosis"
      | "Screening"
      | "Diagnosis"
      | "Overview";
  } = {},
) {
  const effective: Lang = lang ?? "en";
  const copy = pageMeta[path][effective];
  const url = `${SITE_ORIGIN}${path === "/" ? "/" : path}${lang ? `?lang=${lang}` : ""}`;

  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "@id": url,
    url,
    name: copy.ogTitle ?? copy.title,
    headline: copy.title,
    description: copy.description,
    inLanguage: effective === "de" ? "de-DE" : "en-US",
    isPartOf: { "@type": "WebSite", "@id": `${SITE_ORIGIN}/#website` },
    about: {
      "@type": "Drug",
      name: "Retatrutide",
      alternateName: ["Reta", "LY3437943"],
      drugClass: "GLP-1 / GIP / Glucagon triple receptor agonist",
    },
    medicalAudience: [{ "@type": "MedicalAudience", audienceType: "Patient" }],
    lastReviewed: new Date().toISOString().slice(0, 10),
    mainContentOfPage: { "@type": "WebPageElement", cssSelector: "main" },
    ...(opts.specialty && {
      specialty: { "@type": "MedicalSpecialty", name: opts.specialty },
    }),
    ...(opts.aspect && { aspect: opts.aspect }),
  };
}

/**
 * FAQPage schema helper.
 *
 * Stability rules — so Google indexes one FAQPage per language, per page,
 * without treating localized variants as duplicates of each other:
 * - FAQPage `@id` = absolute canonical URL (with `?lang=`) + `#faq[-variant]`.
 *   The `variant` disambiguates pages that legitimately expose more than one
 *   FAQPage block (e.g. `/vergleich` has a compare block and a safety block).
 * - Each Question gets its own absolute `@id` = FAQPage `@id` + `/q-${n}`.
 *   Position-based IDs stay stable even if the wording is tweaked, which is
 *   what Rich Results validation prefers.
 * - Both Question and Answer carry `inLanguage`, matching the FAQPage.
 * - `isPartOf` references the shared WebSite node so all schemas link back
 *   to a single site entity.
 *
 * Answers must be plain text and stay in sync with the on-page copy — Google
 * flags divergent structured data as spam.
 */
export function faqPageSchema(
  path: keyof typeof pageMeta,
  lang: Lang | undefined,
  items: { q: string; a: string }[],
  opts: { variant?: string; name?: string; about?: Record<string, unknown> } = {},
) {
  const effective: Lang = lang ?? "en";
  const inLanguage = effective === "de" ? "de-DE" : "en-US";
  const url = `${SITE_ORIGIN}${path === "/" ? "/" : path}${lang ? `?lang=${lang}` : ""}`;
  const suffix = opts.variant ? `#faq-${opts.variant}` : "#faq";
  const faqId = `${url}${suffix}`;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": faqId,
    inLanguage,
    isPartOf: { "@type": "WebSite", "@id": `${SITE_ORIGIN}/#website` },
    ...(opts.name && { name: opts.name }),
    ...(opts.about && { about: opts.about }),
    mainEntity: items.map((f, i) => ({
      "@type": "Question",
      "@id": `${faqId}/q-${i + 1}`,
      name: f.q,
      inLanguage,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
        inLanguage,
      },
    })),
  };
}

/**
 * Article schema for editorial / long-form medical content pages.
 * Stacks cleanly with MedicalWebPage/BreadcrumbList/FAQPage — Google renders
 * the richest applicable result and ignores the rest.
 *
 * - `headline`, `description`, `image` and `url` are derived from
 *   `pageMeta` + `ogImages` so they always match the visible page.
 * - `author` and `publisher` reference the shared Organization node from
 *   `__root.tsx` (`#organization`) rather than re-declaring the entity.
 * - `datePublished` / `dateModified` default to today; pass explicit ISO
 *   dates when a page has stable authored/updated timestamps.
 */

export function articleSchema(
  path: keyof typeof pageMeta,
  lang: Lang | undefined,
  opts: { datePublished?: string; dateModified?: string; section?: string } = {},
) {
  const effective: Lang = lang ?? "en";
  const copy = pageMeta[path][effective];
  const url = absUrl(path, lang);
  const image = ogImages[path] ?? ogImages["/"];
  const today = new Date().toISOString().slice(0, 10);
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${url}#article`,
    mainEntityOfPage: url,
    headline: copy.ogTitle ?? copy.title,
    description: copy.description,
    inLanguage: effective === "de" ? "de-DE" : "en-US",
    image: [image.url],
    author: { "@id": `${SITE_ORIGIN}/#organization` },
    publisher: { "@id": `${SITE_ORIGIN}/#organization` },
    isPartOf: { "@type": "WebSite", "@id": `${SITE_ORIGIN}/#website` },
    datePublished: opts.datePublished ?? today,
    dateModified: opts.dateModified ?? today,
    ...(opts.section && { articleSection: opts.section }),
  };
}

/**
 * MedicalBusiness / Service schema for the home route.
 * Signals the "medically supervised retatrutide consultation" offering to
 * Google so the home page can surface as a service result.
 */
export function medicalBusinessSchema(lang: Lang | undefined) {
  const effective: Lang = lang ?? "en";
  const de = effective === "de";
  return {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "@id": `${SITE_ORIGIN}/#medicalbusiness`,
    name: "Retatrutid Thailand",
    url: `${SITE_ORIGIN}/`,
    parentOrganization: { "@id": `${SITE_ORIGIN}/#organization` },
    areaServed: [
      { "@type": "Country", name: "Thailand", identifier: "TH" },
      { "@type": "Country", name: "Germany", identifier: "DE" },
      { "@type": "Country", name: "Austria", identifier: "AT" },
      { "@type": "Country", name: "Switzerland", identifier: "CH" },
    ],
    availableLanguage: ["de", "en"],
    medicalSpecialty: "Endocrine",
    priceRange: "$$",
    makesOffer: {
      "@type": "Offer",
      name: de
        ? "Ärztlich begleitete Retatrutid-Behandlung in Bangkok"
        : "Medically supervised retatrutide treatment in Bangkok",
      description: de
        ? "Legaler Zugang zur neuen GLP-1/GIP/Glucagon-Generation über eine Partnerklinik in Bangkok, inkl. deutscher/englischer Betreuung und kostenloser Erstberatung."
        : "Legal access to the new GLP-1/GIP/glucagon generation through a Bangkok partner clinic — with English/German care and a free initial consultation.",
      itemOffered: {
        "@type": "MedicalTherapy",
        name: "Retatrutide (LY3437943) triple-agonist therapy",
        relevantSpecialty: { "@type": "MedicalSpecialty", name: "Endocrine" },
      },
      areaServed: { "@type": "Country", name: "Thailand", identifier: "TH" },
      availability: "https://schema.org/InStock",
    },
  };
}




